import type { AutocompleteProps } from '@mui/material';
import { Autocomplete, TextField } from '@mui/material';
import type { FieldProps } from 'formik';
import { Field } from 'formik';
import React from 'react';

import InputLabel from '@components/InputLabel';

type PickedAutocompleteProps<T> = Pick<
  AutocompleteProps<T, true, true, true>,
  'getOptionLabel' | 'options'
>;

interface FormikMultiAutocompleteFieldProps<
  T,
> extends PickedAutocompleteProps<T> {
  name: string;
  label: string;
  fullWidth?: boolean;
  required?: boolean;
}

function FormikMultiAutocompleteField<T>({
  label,
  name,
  options,
  fullWidth = false,
  required = false,
  ...props
}: FormikMultiAutocompleteFieldProps<T>) {
  return (
    <Field name={name}>
      {({ field, form, meta }: FieldProps<T[]>) => {
        const handleChange = (
          _event: React.SyntheticEvent<Element, Event>,
          value: T[]
        ) => {
          form.setFieldValue(field.name, value, true);
        };

        return (
          <Autocomplete
            multiple
            onChange={handleChange}
            onBlur={field.onBlur}
            getOptionLabel={props.getOptionLabel}
            options={options}
            renderInput={(params) => (
              <>
                <InputLabel
                  label={label}
                  required={required}
                  style={{ color: 'primary.main' }}
                />
                <TextField
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    className: meta.error && meta.touched ? 'error' : undefined,
                  }}
                  error={Boolean(meta.error && meta.touched)}
                  name={field.name}
                  helperText={
                    meta.touched && meta.error ? meta.error : undefined
                  }
                  sx={{
                    '& .MuiInputBase-root': {
                      minHeight: 47,
                    },
                  }}
                />
              </>
            )}
            size="small"
            value={field.value}
            sx={{ width: fullWidth ? '100%' : 'auto' }}
          />
        );
      }}
    </Field>
  );
}

export default FormikMultiAutocompleteField;
